import {
    Avatar,
    BackdropProps,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    Typography,
    useMediaQuery
} from "@mui/material";
import TextInput from "../TextInput/TextInput";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import SearchIcon from '@mui/icons-material/Search';
import Clear from "@mui/icons-material/Clear";
import React, {ReactNode, useContext, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useLazyQuery} from "@apollo/client";
import {GET_GAMES, GET_PAGINATED_GAMES, GET_RECENTLY_PLAYED_GAMES} from "../../store/queries/GetGames";
import {Loader} from "../Loader/Loader";
import {
    Game,
    GameImageType,
    GameStudio,
    GetGamesPaginatedQuery,
    GetGamesQuery,
    GetRecentlyPlayedGamesQuery
} from "../../gql/graphql";
import {BlurredBackdropModal} from "../BlurredBackdropModal/BlurredBackdropModal";
import {GameIcon} from "../GameIcon/GameIcon";
import {RouteContext} from "../../routes/RouteContext";

interface SearchFieldProps extends BackdropProps {
    onClose: () => void;
}

export const SearchField = (props: SearchFieldProps) => {
    const {onClose, open} = props;
    const {t} = useTranslation();
    const inputRef = useRef<HTMLInputElement>();
    const routeContext = useContext(RouteContext);
    const [query, setQuery] = useState("");
    const [filteredGames, setFilteredGames] = useState<(Game | null)[]>([]);
    const [filteredProviders, setFilteredProviders] = useState<(GameStudio | null)[]>([]);
    const {pathname, search} = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [providers, setStudios] = useState<GameStudio[]>([]);
    const [loadgames, games] = useLazyQuery<GetGamesQuery>(GET_GAMES, {
        onCompleted(data: GetGamesQuery) {
            setStudios([...new Set((data?.games as Game[] | null)?.map(x => x?.studio))] as GameStudio[]);
        }
    });
    const [loadRecentlyPlayedGames, recentlyPlayedGames] = useLazyQuery<GetRecentlyPlayedGamesQuery>(GET_RECENTLY_PLAYED_GAMES, {
        variables: {
            first: 3
        }
    });
    const [loadPopularGames, popularGames] = useLazyQuery<GetGamesPaginatedQuery>(GET_PAGINATED_GAMES, {
        variables: {
            first: 3,
            order: {listOrder: 'ASC'}
        }
    });

    useEffect(() => {
        if (open && !games.data) {
            loadgames();
        }
        let AllQueryParams = new URLSearchParams(search);
        const searchTerm = AllQueryParams.get("search");
        handleInputChange(searchTerm ?? "");

        if (open) {
            loadPopularGames();
            if (routeContext.authenticated) {
                loadRecentlyPlayedGames();
            }
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            inputRef.current?.focus();
        }
    }, [inputRef.current])

    const handleInputChange = (value: string) => {
        setQuery(value);
        filterGamesAndProvidersOnQuery(value);
    }

    const clearQuery = () => {
        setQuery("");
        filterGamesAndProvidersOnQuery("");
        inputRef.current?.focus();

        let AllQueryParams = new URLSearchParams(search);
        AllQueryParams.delete("search");
        navigate({pathname, search: AllQueryParams.toString()}, {state: {scrollTo: true}});
    }

    const handleEnter = (key: string) => {
        if (key === "Enter") {
            if (filteredGames.length === 0 && filteredProviders.length === 0) {
                onClose();
                return;
            }

            let AllQueryParams = new URLSearchParams();
            AllQueryParams.set("search", query);

            navigate({pathname: "../games/all", search: AllQueryParams.toString()}, {state: {scrollTo: true}});
            onClose();
        }
    }

    const filterGamesAndProvidersOnQuery = (query: string) => {
        if (!query) {
            setFilteredGames([]);
            setFilteredProviders([]);
            return;
        }

        const queryArray = query.split(' ');
        let filteredGamesArray: (Game | null)[][] = [];
        let filteredProvidersArray: (GameStudio | null)[][] = [];

        queryArray.forEach(queryItem => {
            const filteredGames = (games.data?.games as Game[] | null)?.filter(item => item?.name.toLocaleLowerCase().includes(queryItem.toLocaleLowerCase()));
            let filteredProviders: (GameStudio | null)[] | undefined = [];
            if (query.length > 2) {
                filteredProviders = providers?.filter(item => item?.name.toLocaleLowerCase().includes(queryItem.toLocaleLowerCase()));
            }

            if (filteredGames) filteredGamesArray.push(filteredGames);
            if (filteredProviders) filteredProvidersArray.push(filteredProviders);
        });

        if (filteredGamesArray.length > 0) {
            const filteredGames = filteredGamesArray.reduce((prev, curr) => prev?.filter(x => curr?.includes(x)));
            setFilteredGames(filteredGames);
        }
        if (filteredProvidersArray.length > 0) {
            const filteredProviders = filteredProvidersArray.reduce((prev, curr) => prev?.filter(x => curr?.includes(x)));
            setFilteredProviders(filteredProviders);
        }
    }

    const getHighlightedText = (text: string | undefined, highlightString: string) => {
        const highlightsArray = highlightString.toLocaleLowerCase().split(' ');

        // Split on highlight term and include term into parts, ignore case
        const parts = text?.split(new RegExp(`(${highlightsArray.join('|')})`, 'gi'));
        return (
            <span> 
                {parts?.map((part, i) => (
                    <span key={i}
                          style={highlightsArray.includes(part.toLocaleLowerCase()) ? {fontWeight: 'bolder'} : {}}>
                        {part}
                    </span>
                ))}
            </span>
        );
    }

    return (
        <BlurredBackdropModal
            open={open}
            disablePortal={false}
            handleClose={onClose}
            dialogSx={{
                marginTop: {sm: '82px', xs: '56px'},
                backgroundColor: 'transparent',
                boxShadow: 'none',
                width: '600px',
                m: 4,
                maxWidth: '100%',
                maxHeight: 'calc(100% - 88px)',
                borderRadius: 0
            }}
            dialogWrapperSx={{alignContent: 'flex-start'}}
        >
            <>
                {!games.data && (
                    <Stack alignItems="center" sx={{height: 80}}>
                        <Loader/>
                    </Stack>
                )}
                {games.data && (
                    <TextInput inputRef={inputRef}
                               placeholder={t('search')}
                               autoFocus
                               spellCheck={false}
                               value={query}
                               onChange={e => handleInputChange(e.target.value)}
                               onKeyUp={e => handleEnter(e.key)}
                               sx={{
                                   margin: 0,
                                   backgroundColor: 'white',
                                   borderRadius: '4px',
                                   '& .MuiInputBase-input': {fontSize: '20px', textAlign: 'center'},
                                   '& .MuiInputBase-input.MuiInputBase-inputAdornedStart': {border: 'none'},
                                   '& .MuiOutlinedInput-notchedOutline': {
                                       border: 'none'
                                   },
                               }}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>,
                                   endAdornment: <ClearButton value={!!query} clearValue={clearQuery}/>,
                               }}
                    />
                )}
                <Box sx={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    overflowY: 'auto'
                }}>
                    {!query && (
                        <>
                            {popularGames.data?.gamesPaginated?.nodes && popularGames.data?.gamesPaginated?.nodes.length > 0 && (
                                <>
                                    <Typography variant={'body1'} align="left" fontWeight="bold" sx={{mt: 2, mx: 4}}>
                                        {t('popularGames')}
                                    </Typography>
                                    {popularGames?.data?.gamesPaginated?.nodes?.map((x, index: number) => (
                                        <GameCard item={x as Game} query={query} getHighlightedText={getHighlightedText} onClose={onClose}/>
                                    ))}
                                </>
                            )}
                            {routeContext.authenticated && recentlyPlayedGames.data?.recentlyPlayedGames?.nodes && recentlyPlayedGames.data?.recentlyPlayedGames?.nodes.length > 0 && (
                                <>
                                    <Typography variant={'body1'} align="left" fontWeight="bold" sx={{mt: 2, mx: 4}}>
                                        {t('recentlyPlayed')}
                                    </Typography>
                                    {recentlyPlayedGames?.data?.recentlyPlayedGames?.nodes?.map((x, index: number) => (
                                        <GameCard item={x as Game} query={query} getHighlightedText={getHighlightedText} onClose={onClose}/>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                    {filteredGames.slice(0, filteredProviders.length > 0 ? isSmall ? 2 : 4 : isSmall ? 3 : 5).map(item => (
                        <GameCard item={item} query={query} getHighlightedText={getHighlightedText} onClose={onClose}/>
                    ))}
                    {filteredProviders.slice(0, 1).map(item => (
                        <GameProviderCard item={item} query={query} getHighlightedText={getHighlightedText}
                                          onClose={onClose}/>
                    ))}
                    {filteredGames.length > 0 && (
                        <Stack alignItems="center">
                            <Button
                                sx={{padding: '8px 24px', width: '100%', borderRadius: 0}}
                                onClick={() => handleEnter("Enter")}>
                                <Typography variant={'body1'}>
                                    {t('See all games')} ({filteredGames.length})
                                </Typography>
                            </Button>
                        </Stack>
                    )}
                    {filteredGames.length === 0 && filteredProviders.length === 0 && query && (
                        <Button onClick={onClose}
                                sx={{
                                    width: '100%',
                                    borderRadius: 0,
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    color: '#000000',
                                    padding: '16px 24px'
                                }}>
                            <Stack sx={{width: '100%'}}>
                                <Typography variant={'h6'} align="center" sx={(theme) => ({
                                    marginTop: {xs: 1, md: 2},
                                    marginBottom: 2,
                                    textTransform: 'none',

                                })}>{t('No results found')}</Typography>
                                <Typography sx={{marginBottom: {xs: 1, md: 2}}}
                                            align="center">{t('No results found for this search query.')}</Typography>
                            </Stack>
                        </Button>
                    )}
                </Box>
            </>
        </BlurredBackdropModal>
    )
}

interface ClearButtonProps {
    value: boolean;
    clearValue?: any;
}

const ClearButton = (props: ClearButtonProps) => {
    const {value, clearValue} = props;

    return (
        <InputAdornment position="end"
                        sx={{visibility: value ? 'visible' : 'hidden'}}>
            <IconButton onClick={clearValue} sx={{marginRight: '-12px'}}>
                <Clear/>
            </IconButton>
        </InputAdornment>
    )
}

interface GameCardProps {
    item: Game | null;
    query: string;
    getHighlightedText: (text: string | undefined, highlightString: string) => ReactNode;
    onClose: () => void;
}

const GameCard = (props: GameCardProps) => {
    const {item, query, getHighlightedText, onClose} = props;
    const navigate = useNavigate();

    const goToItem = (item: Game | null) => {
        if (item) {
            navigate({pathname: `/games/${item.studio.urlName}/${item.urlName}`}, {state: {scrollTo: true}});
            onClose();
        }
    }

    return (
        <Box key={item?.id}>
            <Button onClick={() => goToItem(item)}
                    sx={{
                        width: '100%',
                        borderRadius: 0,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        color: '#000000'
                    }}>
                <Stack direction="row" alignItems="center" sx={{padding: '16px 24px', width: '100%'}}>
                    <Avatar src={item?.images.find(x => x.type === "FEATURED_THUMBNAIL")?.url}
                            variant="square"
                            sx={{
                                height: '50px',
                                width: '50px',
                                borderRadius: '4px'
                            }}/>
                    <Stack alignItems="flex-start" flex="1" sx={{marginX: '16px'}}>
                        <Typography variant={'body1'} align="left">
                            {getHighlightedText(item?.name, query)}
                        </Typography>
                        {item?.studio.logoUrl ? (
                            <Box component="img"
                                 sx={{height: "18px", width: "auto", marginTop: '2px'}}
                                 src={item?.studio.logoUrl}/>
                        ) : (
                            <Typography variant={'body2'} align="left">
                                {item?.studio.name}
                            </Typography>
                        )}
                    </Stack>
                    <ArrowForwardIcon sx={{height: '16px', width: '16px'}}/>
                </Stack>
            </Button>
            <Divider/>
        </Box>
    )
}

interface GameProviderCardProps {
    item: GameStudio | null;
    query: string;
    getHighlightedText: (text: string | undefined, highlightString: string) => ReactNode;
    onClose: () => void;
}

const GameProviderCard = (props: GameProviderCardProps) => {
    const {item, query, getHighlightedText, onClose} = props;
    const navigate = useNavigate();
    const {pathname} = useLocation();

    const showAllProviderGames = (item: GameStudio | null) => {
        if (item) {
            let AllQueryParams = new URLSearchParams();
            AllQueryParams.set('providers', item.name);
            navigate({pathname, search: AllQueryParams.toString()}, {state: {scrollTo: true}})
            onClose();
        }
    }

    return (
        <Box key={item?.id}>
            <Button onClick={() => showAllProviderGames(item)}
                    sx={{
                        width: '100%',
                        borderRadius: 0,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        color: '#000000'
                    }}>
                <Stack direction="row" alignItems="center" sx={{padding: '16px 24px', width: '100%'}}>
                    <Avatar src={item?.logoFaviconUrl}
                            variant="square"
                            sx={{
                                height: '50px',
                                width: '50px',
                                borderRadius: '4px'
                            }}/>
                    <Stack alignItems="flex-start" flex="1" sx={{marginX: '16px'}}>
                        <Typography variant={'body1'} align="left">
                            See all games of <span
                            style={{textTransform: "capitalize"}}>"{getHighlightedText(item?.name, query)}"</span>
                        </Typography>
                    </Stack>
                    <ArrowForwardIcon sx={{height: '16px', width: '16px'}}/>
                </Stack>
            </Button>
            <Divider/>
        </Box>
    )
}